// SendDealModal — выбор: в бухгалтерию напрямую или через юристов
import React, { useState } from 'react';
import { X, Scale, Calculator, ArrowRight, AlertCircle } from 'lucide-react';
import api from '../../../api/client';
import { useToast } from '../../../contexts/ToastContext';

interface Props {
  deal: any;
  onClose: () => void;
  onSuccess: () => void;
}

const SendDealModal: React.FC<Props> = ({ deal, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [mode, setMode] = useState<'legal' | 'accounting' | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!mode) return;
    setLoading(true);
    try {
      const endpoint = mode === 'legal'
        ? `/sales/deals/${deal._id}/send-to-legal`
        : `/sales/deals/${deal._id}/send-to-accounting`;
      const r = await api.post(endpoint);
      showToast(
        mode === 'legal' ? '⚖️ Отправлено юристам' : '📤 Задача создана для бухгалтера',
        'success'
      );
      onSuccess();
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Ошибка', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div>
            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#1a1a2e' }}>
              Что делаем дальше?
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
              Сделка: <b>{deal.title}</b> · {((deal.amount || 0).toLocaleString('ru-RU'))} ₽
            </p>
          </div>
          <button onClick={onClose} style={btnIcon}><X size={20} /></button>
        </div>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{
            padding: 14,
            background: 'rgba(245,158,11,0.08)',
            borderRadius: 12,
            border: '1px solid rgba(245,158,11,0.3)',
            display: 'flex',
            gap: 10,
            fontSize: 12,
            color: '#92400e',
            lineHeight: 1.6,
          }}>
            <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <b>⚖️ Как это работает:</b><br/>
              Сначала договор проверяет <b>юрист</b>. После его одобрения сделка автоматически 
              отправляется в <b>бухгалтерию</b>. Если договор уже проверен — можно сразу в бухгалтерию.
            </div>
          </div>

          {/* Вариант 1: Юристы */}
          <button
            onClick={() => setMode('legal')}
            style={{
              padding: 16,
              borderRadius: 14,
              border: mode === 'legal' ? '2px solid #6366f1' : '2px solid transparent',
              background: mode === 'legal' ? 'rgba(99,102,241,0.08)' : '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              textAlign: 'left',
              fontFamily: 'inherit',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              transition: 'all 0.15s',
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              color: '#fff', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: 22, flexShrink: 0,
            }}>
              <Scale size={24} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>
                ⚖️ Отправить на проверку юристу
              </div>
              <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                Юрист проверит договор и либо одобрит, либо вернёт с правками.<br/>
                После одобрения — <b>сделка вернётся к вам</b> для отправки в бухгалтерию.
              </div>
            </div>
          </button>

          {/* Вариант 2: Бухгалтерия */}
          <button
            onClick={() => setMode('accounting')}
            style={{
              padding: 16,
              borderRadius: 14,
              border: mode === 'accounting' ? '2px solid #10b981' : '2px solid transparent',
              background: mode === 'accounting' ? 'rgba(16,185,129,0.08)' : '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              textAlign: 'left',
              fontFamily: 'inherit',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              transition: 'all 0.15s',
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              color: '#fff', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: 22, flexShrink: 0,
            }}>
              <Calculator size={24} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>
                📤 Сразу в бухгалтерию
              </div>
              <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                Только если договор <b>уже согласован юристом</b>.<br/>
                Бухгалтерия проверит контрагента и выставит счёт.
              </div>
            </div>
          </button>
        </div>

        <div style={footer}>
          <button onClick={onClose} style={btnOutline}>Отмена</button>
          <button
            onClick={handleSubmit}
            disabled={!mode || loading}
            style={{
              ...btnPrimary,
              background: mode === 'legal'
                ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                : mode === 'accounting'
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : 'linear-gradient(135deg, #6a11cb, #2575fc)',
              opacity: !mode || loading ? 0.5 : 1,
              cursor: !mode || loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Отправляем...' : mode === 'legal' ? '⚖️ Отправить юристам' : mode === 'accounting' ? '📤 В бухгалтерию' : <>Далее <ArrowRight size={14} /></>}
          </button>
        </div>
      </div>
    </div>
  );
};

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(15,23,42,0.55)',
  backdropFilter: 'blur(6px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1200, padding: 20,
};
const modal: React.CSSProperties = {
  background: '#fff', borderRadius: 20, maxWidth: 600, width: '100%',
  display: 'flex', flexDirection: 'column',
  boxShadow: '0 25px 60px rgba(15,23,42,0.3)',
  overflow: 'hidden',
};
const header: React.CSSProperties = {
  padding: '20px 24px', borderBottom: '1px solid #f1f5f9',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
};
const footer: React.CSSProperties = {
  padding: '16px 24px', borderTop: '1px solid #f1f5f9',
  display: 'flex', gap: 10, justifyContent: 'flex-end', background: '#f8fafc',
};
const btnIcon: React.CSSProperties = {
  background: 'rgba(255,255,255,0.85)', border: '1px solid #e2e8f0',
  borderRadius: 12, padding: 10, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const btnPrimary: React.CSSProperties = {
  background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
  color: '#fff', border: 'none', borderRadius: 12,
  padding: '12px 24px', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
  fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
};
const btnOutline: React.CSSProperties = {
  background: 'rgba(255,255,255,0.7)', border: '1px solid #e2e8f0',
  borderRadius: 12, padding: '10px 16px', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
  fontSize: 13, fontWeight: 600, color: '#475569', fontFamily: 'inherit',
};

export default SendDealModal;
